import React, { useEffect, useState } from 'react';

import styles from './Organization.module.css';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import Top from './Top';

import { Button, Loader } from '@mantine/core';

import Globe from '@icons/Globe.svg';
import UserCheck from '@icons/UserCheck.svg';
import Building from '@icons/Building.svg';
import GlobeWhite from '@icons/GlobeWhite.svg';
import UserCheckWhite from '@icons/UserCheckWhite.svg';
import BuildingWhite from '@icons/BuildingWhite.svg';
import Limit from '@icons/Limit.svg';
import LimitWhite from '@icons/LimitWhite.svg';

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PlacesTable from './PlacesTable';
import AdmitForm from './AdmitForm';
import Common from './Common';
import useOrganization from './hooks/useOrganization';
import LimitsForm from './LimitsForm'
import { useForm } from '@mantine/form';

const Organization = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState();
  const { id } = useParams();
  const navigate = useNavigate();
  const [
    { organization, permissions, places, filteredPlaces, sortName, sortValue, limits },
    { fetch, fetchPermissions, fetchPlaces, sort, updateLimits, filterPlaces, updatePermissions, fetchLimits }
  ] = useOrganization(t, navigate, searchParams);

  const getPermission = (type) => {
    return permissions.find(permission => permission.featureType === type);
  }

  const getLimit = (type) => {
    return limits.find(permission => permission.type === type);
  }

  const limitsForm = useForm({
    mode: 'uncontrolled',
  });

  const form = useForm({
    mode: 'uncontrolled',
  });

  let pageInitial = 'common';
  let queryName = 'name'
  let queryValue = -1

  for (const entry of searchParams.entries()) {
    const [param, value] = entry;

    if (param === 'page') {
      pageInitial = value
    }

    if (param === 'sortValue') {
      queryValue = Number(value)
    }

    if (param === 'sortName') {
      queryName = value
    }
  }

  const [page, setPage] = useState(pageInitial);

  useEffect(() => {
    fetch(id);
    fetchPermissions(id);
    fetchPlaces(id);
    fetchLimits(id);
  }, [id])

  useEffect(() => {
    if (permissions) {
      form.setValues({
        elements: [
          {
            name: t('organization.permission.claim'),
            ...getPermission('CLAIM')
          },
          {
            name: t('organization.permission.place'),
            ...getPermission('PLACE')
          },
          {
            name: t('organization.permission.service'),
            ...getPermission('SERVICE')
          },
          {
            name: t('organization.permission.referral'),
            ...getPermission('REFERRAL')
          },
          {
            name: t('organization.permission.promo'),
            ...getPermission('PROMO')
          }
        ]
      })
    }

    if (limits) {
      limitsForm.setValues({
        elements: [
          {
            name: t('organization.limits.CLAIM'),
            ...getLimit('CLAIM')
          },
          {
            name: t('organization.limits.place'),
            ...getLimit('PLACE')
          },
          {
            name: t('organization.limits.service'),
            ...getLimit('SERVICE')
          },
          {
            name: t('organization.limits.refferal'),
            ...getLimit('REFERRAL')
          },
          {
            name: t('organization.limits.promo'),
            ...getLimit('PROMO')
          }
        ]
      })
    }
  }, [permissions, limits])


  const handleSetPage = (value) => {
    setPage(value);

    const queryValueString = queryValue ? `&sortValue=${queryValue}` : '';
    const queryNameString = queryName ? `&sortName=${queryName}` : '';

    navigate(`/organizations/${id}?page=${value}${queryValueString}${queryNameString}`, { replace: true })
  }

  const onUpdateLimits = async () => {
    const values = limitsForm.getValues();
    const dataReq = {
      CLAIM: values.elements.find(val => val.type === 'CLAIM').limit,
      PLACE: values.elements.find(val => val.type === 'PLACE').limit,
      SERVICE: values.elements.find(val => val.type === 'SERVICE').limit,
      REFERRAL: values.elements.find(val => val.type === 'REFERRAL').limit,
      PROMO: values.elements.find(val => val.type === 'PROMO').limit,
    }

    return await updateLimits(id, dataReq)
  }

  const handleAdmitSubmit = async () => {
    const values = form.getValues();
    setLoading(true);
    if (page === 'admit') {
      await updatePermissions(id, values.elements)
    }

    if (page === 'limits') {
      await onUpdateLimits();
    }

    setLoading(false)
  }

  return (
    <div className={styles.wrapper}>
      {organization && <Top loading={loading} organization={organization} onSubmit={handleAdmitSubmit} />}

      <div className={styles.content}>
        <div className={styles.menu}>
          <Button
            leftSection={<img src={page === 'common' ? GlobeWhite : Globe} />}
            fullWidth
            justify='left'
            className={cn(styles.button, page === 'common' && styles.active)}
            onClick={() => handleSetPage('common')}
          >
            {t('common')}
          </Button>

          <Button
            leftSection={<img src={page === 'admit' ? UserCheckWhite : UserCheck} />}
            fullWidth
            justify='left'
            className={cn(styles.button, page === 'admit' && styles.active)}
            onClick={() => handleSetPage('admit')}
          >
            {t('admit')}
          </Button>

          <Button
            leftSection={<img src={page === 'places' ? BuildingWhite : Building} />}
            fullWidth
            className={cn(styles.button, page === 'places' && styles.active)}
            onClick={() => handleSetPage('places')}
            justify='left'
          >
            {t('places')}
          </Button>

          <Button
            leftSection={<img src={page === 'limits' ? LimitWhite : Limit} />}
            fullWidth
            className={cn(styles.button, page === 'limits' && styles.active)}
            onClick={() => handleSetPage('limits')}
            justify='left'
          >
            {t('limits')}
          </Button>
        </div>

        {page === 'common' && !organization && <div className={styles.loader}><Loader /></div>}

        {page === 'places' && !places && <div className={styles.loader}><Loader /></div>}

        {page === 'admit' && !permissions && <div className={styles.loader}><Loader /></div>}

        {page === 'limits' && !limits && <div className={styles.loader}><Loader /></div>}

        <div className={styles.form}>
          {page === 'common' && organization && <Common organization={organization} />}

          {page === 'places' && places && (
            <PlacesTable
              places={filteredPlaces}
              sortName={sortName}
              id={id}
              organization={organization}
              filterPlaces={filterPlaces}
              sortValue={sortValue}
              sort={sort}
            />
          )}

          {page === 'admit' && form.getValues()?.elements?.length > 0 && (
            <AdmitForm
              id={id}
              onSubmit={handleAdmitSubmit}
              loading={loading}
              form={form}
              updatePermissions={updatePermissions}
              permissions={permissions}
            />
          )}

          {page === 'limits' && limitsForm.getValues()?.elements?.length && (
            <LimitsForm
              id={id}
              updateLimits={updateLimits}
              form={limitsForm}
              limits={limits}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Organization;
